import { useThree } from '@react-three/fiber';
import { omit } from 'lodash-es';
import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import type { Vector3Tuple } from 'three';

import { TOOL_CONST } from '@/constant';
import { useToolMainCameraStore } from '@/store';
import { toRadian } from '@/util';

type useInitializeCameraProps = {
  initCameraOptions?: {
    position?: Vector3Tuple;
    rotation?: Vector3Tuple;
    up?: Vector3Tuple;
    far?: number;
    near?: number;
    orthographic?: {
      zoom?: number;
    };
    perpective?: {
      zoom?: number;
    };
  };
};

const TOOL_CAMERA_VIEW_MODE = TOOL_CONST.TOOL_CAMERA_VIEW_MODE;

const useInitializeCamera = ({
  initCameraOptions,
}: useInitializeCameraProps) => {
  const { getMode, getSavedCameraInfo, modeIndex, clear } =
    useToolMainCameraStore();
  const isInitializedCamera = useRef(false);
  const mode = useMemo(getMode, [modeIndex]);
  const { camera, controls } = useThree();
  const isTopMode = useMemo(() => mode === TOOL_CAMERA_VIEW_MODE.TOP, [mode]);
  const isSideOrFrontMode = useMemo(
    () =>
      mode === TOOL_CAMERA_VIEW_MODE.SIDE ||
      mode === TOOL_CAMERA_VIEW_MODE.FRONT,
    [mode],
  );

  useLayoutEffect(() => {
    /**
     * DESCRIPTION: mode 가 변경됐지만 camera 가 이전 상태인 경우가 발생하여 넣어둔 코드
     */
    if (isTopMode && camera.isPerspectiveCamera) return;

    if (
      !isInitializedCamera.current &&
      camera.parent != null &&
      controls != null &&
      initCameraOptions
    ) {
      const options = omit(initCameraOptions, [
        'far',
        'near',
        'orthographic',
        'perpective',
      ]);

      options.up && camera.up.set(...options.up);
      options.rotation && camera.rotation.set(...options.rotation);

      camera.updateProjectionMatrix();
      isInitializedCamera.current = true;
    }
  }, [camera, controls, initCameraOptions, mode]);

  useEffect(() => {
    if (!controls || !isInitializedCamera.current || !mode) return;

    const { azimuth, polar } = TOOL_CONST.ROTATION_VIEW_MODE_MAP[mode];

    const isInitRotationNeed =
      mode !== TOOL_CAMERA_VIEW_MODE.DEFAULT || !getSavedCameraInfo(mode);
    const isPerspectiveCamera = camera.isPerspectiveCamera;

    controls.maxAzimuthAngle = toRadian(360);
    controls.minAzimuthAngle = toRadian(1);
    controls.maxPolarAngle = toRadian(180);
    controls.minPolarAngle = toRadian(-180);

    if (isTopMode && !isPerspectiveCamera) {
      controls.maxPolarAngle = polar;
      controls.minPolarAngle = polar;
    }

    if (isSideOrFrontMode) {
      controls.maxPolarAngle = polar;
      controls.minPolarAngle = polar;
      controls.maxAzimuthAngle = azimuth;
      controls.minAzimuthAngle = azimuth;
    }

    if (isInitRotationNeed) {
      controls.setPolarAngle(polar);
      controls.setAzimuthalAngle(azimuth);
      controls.update();
    }
  }, [camera, controls, mode]);

  useEffect(() => {
    isInitializedCamera.current = false;
  }, [mode]);

  useEffect(() => {
    return () => clear();
  }, []);
};

export default useInitializeCamera;
